import {
   TodaySalesYesterdaySalesResponse,
   TotalSalesThisMonthAndLastMonthResponse,
   TotalSalesThisWeekAndLastWeekResponse,
} from '@/api/transaction';
import { colors } from '@/constants/colors';
import IconsLib from '@/utils/IconsLib';
import { currencyFormat } from '@/utils/currencyFormatter';
import React from 'react';
import styled from 'styled-components';

// type TotalSalesTodayAndYesterday = {
//    today: number;
//    yesterday: number;
//    percentage: number;
// };

// type TotalSalesThisWeekAndLastWeek = {
//    thisweek: number;
//    lastweek: number;
//    percentage: number;
// };

// type TotalSalesThisMonthAndLastMonth = {

// }

interface Props {
   totalSalesTodayAndYesterday: TodaySalesYesterdaySalesResponse;
   totalSalesThisWeekAndLastWeek: TotalSalesThisWeekAndLastWeekResponse;
   totalSalesThisMonthAndLastMonth: TotalSalesThisMonthAndLastMonthResponse;
}

const CardSalesTop = ({
   totalSalesTodayAndYesterday,
   totalSalesThisWeekAndLastWeek,
   totalSalesThisMonthAndLastMonth,
}: // totalSalesThisMonthAndLastMonth,
Props) => {
   const computePct = (cur: number, prev: number): number | null => {
      const c = Number(cur) || 0;
      const p = Number(prev) || 0;
      if (p === 0 && c === 0) return 0;
      if (p === 0) return null; // no baseline — render as "New"
      return ((c - p) / p) * 100;
   };

   const tiles = [
      {
         label: 'Today',
         prevLabel: 'Yesterday',
         value: totalSalesTodayAndYesterday.today,
         prev: totalSalesTodayAndYesterday.yesterday,
         pct: computePct(
            totalSalesTodayAndYesterday.today,
            totalSalesTodayAndYesterday.yesterday
         ),
      },
      {
         label: 'This Week',
         prevLabel: 'Last week',
         value: totalSalesThisWeekAndLastWeek.thisWeek,
         prev: totalSalesThisWeekAndLastWeek.lastWeek,
         pct: computePct(
            totalSalesThisWeekAndLastWeek.thisWeek,
            totalSalesThisWeekAndLastWeek.lastWeek
         ),
      },
      {
         label: 'This Month',
         prevLabel: 'Last month',
         value: totalSalesThisMonthAndLastMonth.thisMonth,
         prev: totalSalesThisMonthAndLastMonth.lastMonth,
         pct: computePct(
            totalSalesThisMonthAndLastMonth.thisMonth,
            totalSalesThisMonthAndLastMonth.lastMonth
         ),
      },
   ];

   const trendOf = (pct: number | null): 'up' | 'down' | 'flat' | 'new' =>
      pct === null ? 'new' : pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';

   const trendIcon = (trend: 'up' | 'down' | 'flat' | 'new') =>
      trend === 'up'
         ? 'arrow-up-line'
         : trend === 'down'
         ? 'arrow-down-line'
         : trend === 'new'
         ? 'sparkling-line'
         : 'subtract-line';

   const trendText = (pct: number | null) =>
      pct === null ? 'New' : `${Math.abs(pct).toFixed(0)}%`;

   return (
      <Wrapper>
         <ItemContent>
            {tiles.map((t, i) => {
               const trend = trendOf(t.pct);
               return (
                  <Item key={i}>
                     <IconsLib icon="calendar-2-fill" size="30px" color="white" />
                     <ItemMain>
                        <ItemDayRow>
                           <ItemDay>{t.label}</ItemDay>
                           <TrendPill $trend={trend}>
                              <IconsLib
                                 icon={trendIcon(trend)}
                                 size="12px"
                                 color="white"
                              />
                              {trendText(t.pct)}
                           </TrendPill>
                        </ItemDayRow>
                        <ItemValue>{currencyFormat(t.value, true)}</ItemValue>
                        <ItemContentPrevious>
                           <ItemPrevLabel>{t.prevLabel}</ItemPrevLabel>
                           <ItemPrevValue>
                              {currencyFormat(t.prev, true)}
                           </ItemPrevValue>
                        </ItemContentPrevious>
                     </ItemMain>
                  </Item>
               );
            })}
         </ItemContent>

         <MobileTiles>
            {tiles.map((t, i) => {
               const trend = trendOf(t.pct);
               return (
                  <MobileTile key={i}>
                     <MobileTileLabel>{t.label}</MobileTileLabel>
                     <MobileTileValue>
                        {currencyFormat(t.value, true)}
                     </MobileTileValue>
                     <MobileTileFoot>
                        <TrendPill $trend={trend}>
                           <IconsLib
                              icon={trendIcon(trend)}
                              size="12px"
                              color="white"
                           />
                           {trendText(t.pct)}
                        </TrendPill>
                        <MobilePrevValue>
                           vs {currencyFormat(t.prev, true)}
                        </MobilePrevValue>
                     </MobileTileFoot>
                  </MobileTile>
               );
            })}
         </MobileTiles>
      </Wrapper>
   );
};

const Wrapper = styled.div``;

const Item = styled.div`
   padding: 2rem;
   display: flex;
   align-items: center;
   gap: 5px;
   @media (max-width: 768px) {
      display: none;
   }
`;

const ItemDay = styled.p`
   font-size: 14px;
   color: white;
`;
const ItemDayRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   margin-bottom: 4px;
`;
const ItemContent = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   align-items: center;
   background: ${colors.PRIMARY};
   border-radius: 8px;
   @media (max-width: 768px) {
      display: none;
   }
`;
const ItemMain = styled.div``;
const ItemValue = styled.p`
   font-size: 24px;
   font-weight: 700;
   color: white;
   line-height: 1.15;
`;
const ItemContentPrevious = styled.div`
   display: flex;
   align-items: center;
   gap: 5px;
`;
const ItemPrevLabel = styled.p`
   font-size: 14px;
   color: ${colors.GRAY1};
   color: white;
`;
const ItemPrevValue = styled.p`
   font-size: 14px;
   color: ${colors.GRAY1};
   color: white;
`;

const MobileTiles = styled.div`
   display: none;
   @media (max-width: 768px) {
      display: grid;
      grid-template-columns: 1fr;
      gap: 8px;
      padding: 0 12px;
   }
`;

const MobileTile = styled.div`
   background: linear-gradient(135deg, ${colors.PRIMARY} 0%, #5a78ff 100%);
   border-radius: 14px;
   padding: 12px 14px;
   display: flex;
   flex-direction: column;
   gap: 2px;
   color: white;
   box-shadow: 0 4px 12px rgba(55, 96, 249, 0.22);
`;

const MobileTileLabel = styled.span`
   font-size: 11px;
   font-weight: 500;
   opacity: 0.85;
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const MobileTileValue = styled.span`
   font-size: 22px;
   font-weight: 700;
   line-height: 1.15;
`;

const MobileTileFoot = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   margin-top: 4px;
`;

const TrendPill = styled.span<{ $trend: 'up' | 'down' | 'flat' | 'new' }>`
   display: inline-flex;
   align-items: center;
   gap: 2px;
   background: ${({ $trend }) =>
      $trend === 'up'
         ? 'rgba(25,135,84,0.9)'
         : $trend === 'down'
         ? 'rgba(220,53,69,0.9)'
         : $trend === 'new'
         ? 'rgba(255,193,7,0.9)'
         : 'rgba(255,255,255,0.2)'};
   color: white;
   font-size: 11px;
   font-weight: 600;
   padding: 2px 8px;
   border-radius: 999px;
`;

const MobilePrevValue = styled.span`
   font-size: 11px;
   opacity: 0.82;
`;

export default CardSalesTop;
